import { FC, useState } from 'react';

import { useForm } from '@inertiajs/react';
import { ChevronDown, ChevronUp, Package, Save, Truck } from 'lucide-react';

import { DeliveryMethodSectionHeader } from './delivery_profiles/DeliveryMethodSectionHeader';
import { DeliveryProfileField } from './delivery_profiles/DeliveryProfileField';
import { NovaPoshtaCitySelect } from '../../components/nova_poshta/NovaPoshtaCitySelect';
import { NovaPoshtaWarehouseSelect } from '../../components/nova_poshta/NovaPoshtaWarehouseSelect';
import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { UkrposhtaOfficeSelect } from '../../components/ukrposhta/UkrposhtaOfficeSelect';
import { UkrposhtaSettlementSelect } from '../../components/ukrposhta/UkrposhtaSettlementSelect';
import { profileDeliveryRoute } from '../../models/routes';
import { ShippingMethod } from '../../types';

export interface SavedDeliveryProfile {
    id?: number;
    method: string;
    recipient_name?: string;
    recipient_phone?: string;
    city?: string;
    region?: string;
    delivery_point_type?: string;
    delivery_point_number?: string;
    post_office?: string;
    nova_poshta_city_ref?: string;
    nova_poshta_city_name?: string;
    nova_poshta_warehouse_ref?: string;
    nova_poshta_warehouse_number?: string;
    nova_poshta_warehouse_address?: string;
    ukrposhta_settlement_ref?: string;
    ukrposhta_settlement_name?: string;
    ukrposhta_post_office_ref?: string;
    ukrposhta_post_office_index?: string;
    ukrposhta_post_office_address?: string;
    ukrposhta_post_office_name?: string;
}

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    availableMethods?: ShippingMethod[];
    novaPoshta: SavedDeliveryProfile | null;
    ukrposhta: SavedDeliveryProfile | null;
}

interface ProfileForm {
    [key: string]: string;
    recipient_name: string;
    recipient_phone: string;
    city: string;
    region: string;
    delivery_point_type: string;
    delivery_point_number: string;
    post_office: string;
    nova_poshta_city_ref: string;
    nova_poshta_city_name: string;
    nova_poshta_warehouse_ref: string;
    nova_poshta_warehouse_number: string;
    nova_poshta_warehouse_address: string;
    ukrposhta_settlement_ref: string;
    ukrposhta_settlement_name: string;
    ukrposhta_post_office_ref: string;
    ukrposhta_post_office_index: string;
    ukrposhta_post_office_address: string;
    ukrposhta_post_office_name: string;
}

type FormErrors = Record<string, string>;

function toForm(profile: SavedDeliveryProfile | null): ProfileForm {
    return {
        recipient_name: profile?.recipient_name ?? '',
        recipient_phone: profile?.recipient_phone ?? '',
        city: profile?.city ?? '',
        region: profile?.region ?? '',
        delivery_point_type: profile?.delivery_point_type ?? 'branch',
        delivery_point_number: profile?.delivery_point_number ?? '',
        post_office: profile?.post_office ?? '',
        nova_poshta_city_ref: profile?.nova_poshta_city_ref ?? '',
        nova_poshta_city_name: profile?.nova_poshta_city_name ?? '',
        nova_poshta_warehouse_ref: profile?.nova_poshta_warehouse_ref ?? '',
        nova_poshta_warehouse_number:
            profile?.nova_poshta_warehouse_number ?? '',
        nova_poshta_warehouse_address:
            profile?.nova_poshta_warehouse_address ?? '',
        ukrposhta_settlement_ref: profile?.ukrposhta_settlement_ref ?? '',
        ukrposhta_settlement_name: profile?.ukrposhta_settlement_name ?? '',
        ukrposhta_post_office_ref: profile?.ukrposhta_post_office_ref ?? '',
        ukrposhta_post_office_index: profile?.ukrposhta_post_office_index ?? '',
        ukrposhta_post_office_address:
            profile?.ukrposhta_post_office_address ?? '',
        ukrposhta_post_office_name: profile?.ukrposhta_post_office_name ?? '',
    };
}

function extractMethodErrors(
    errors: unknown,
    method: 'nova_poshta' | 'ukrposhta',
): FormErrors {
    if (!errors || typeof errors !== 'object') return {};

    const errorHash = errors as Record<string, unknown>;
    const scopedErrors = errorHash[method];

    if (
        scopedErrors &&
        typeof scopedErrors === 'object' &&
        !Array.isArray(scopedErrors)
    ) {
        return Object.fromEntries(
            Object.entries(scopedErrors as Record<string, unknown>).map(
                ([key, value]) => [key, String(value)],
            ),
        );
    }

    return Object.fromEntries(
        Object.entries(errorHash)
            .filter(([, value]) => typeof value === 'string')
            .map(([key, value]) => [key, value as string]),
    );
}

export const DeliveryProfilesForm: FC<Props> = ({
    isOpen,
    onToggle,
    availableMethods,
    novaPoshta,
    ukrposhta,
}) => {
    const showUkrposhta = availableMethods?.includes('ukrposhta') ?? true;
    const [npForm, setNpForm] = useState<ProfileForm>(() => toForm(novaPoshta));
    const [upForm, setUpForm] = useState<ProfileForm>(() => toForm(ukrposhta));
    const [npErrors, setNpErrors] = useState<FormErrors>({});
    const [upErrors, setUpErrors] = useState<FormErrors>({});
    const [npSuccess, setNpSuccess] = useState('');
    const [upSuccess, setUpSuccess] = useState('');
    const npSubmitForm = useForm<{ delivery_profile: ProfileForm }>({
        delivery_profile: toForm(novaPoshta),
    });
    const upSubmitForm = useForm<{ delivery_profile: ProfileForm }>({
        delivery_profile: toForm(ukrposhta),
    });

    const handleNpSave = () => {
        setNpSuccess('');
        npSubmitForm.transform(() => ({ delivery_profile: npForm }));
        npSubmitForm.patch(profileDeliveryRoute('nova_poshta'), {
            preserveScroll: true,
            onSuccess: () => {
                setNpErrors({});
                setTimeout(() => setNpSuccess(''), 3000);
            },
            onError: (errors) => {
                setNpErrors(extractMethodErrors(errors, 'nova_poshta'));
            },
        });
    };

    const handleUpSave = () => {
        setUpSuccess('');
        upSubmitForm.transform(() => ({ delivery_profile: upForm }));
        upSubmitForm.patch(profileDeliveryRoute('ukrposhta'), {
            preserveScroll: true,
            onSuccess: () => {
                setUpErrors({});
                setTimeout(() => setUpSuccess(''), 3000);
            },
            onError: (errors) => {
                setUpErrors(extractMethodErrors(errors, 'ukrposhta'));
            },
        });
    };

    const handleClear = (
        method: 'nova_poshta' | 'ukrposhta',
        resetForm: () => void,
    ) => {
        if (!confirm('Очистити збережені дані доставки?')) return;
        const submitForm =
            method === 'nova_poshta' ? npSubmitForm : upSubmitForm;

        submitForm.delete(profileDeliveryRoute(method), {
            preserveScroll: true,
            onSuccess: () => resetForm(),
        });
    };

    return (
        <Card id="delivery-details" className="h-fit scroll-mt-4">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Truck className="h-5 w-5 text-primary" />
                        Дані доставки
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Збережені адреси для автозаповнення при оформленні замовлень
                </CardDescription>
            </CardHeader>
            {isOpen && (
                <CardContent className="space-y-8">
                    {/* Nova Poshta */}
                    <div className="space-y-4">
                        <DeliveryMethodSectionHeader
                            title="Нова Пошта"
                            icon={Truck}
                            showClear={!!novaPoshta}
                            onClear={() =>
                                handleClear('nova_poshta', () =>
                                    setNpForm(toForm(null)),
                                )
                            }
                        />

                        {npSuccess && (
                            <p className="text-sm font-medium text-green-600">
                                {npSuccess}
                            </p>
                        )}

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <DeliveryProfileField
                                id="np_recipient_name"
                                label="ПІБ отримувача"
                                value={npForm.recipient_name}
                                onChange={(v) =>
                                    setNpForm({
                                        ...npForm,
                                        recipient_name: v,
                                    })
                                }
                                error={npErrors.recipient_name}
                                placeholder="Іванов Іван Іванович"
                                required
                            />
                            <DeliveryProfileField
                                id="np_recipient_phone"
                                label="Телефон"
                                value={npForm.recipient_phone}
                                onChange={(v) =>
                                    setNpForm({
                                        ...npForm,
                                        recipient_phone: v,
                                    })
                                }
                                error={npErrors.recipient_phone}
                                placeholder="+380991234567"
                                required
                            />
                        </div>

                        <NovaPoshtaCitySelect
                            value={npForm.nova_poshta_city_name || npForm.city}
                            onSelect={(city) => {
                                setNpForm((prev) => ({
                                    ...prev,
                                    city: city.name,
                                    nova_poshta_city_ref: city.ref,
                                    nova_poshta_city_name: city.name,
                                    delivery_point_number: '',
                                    nova_poshta_warehouse_ref: '',
                                    nova_poshta_warehouse_number: '',
                                    nova_poshta_warehouse_address: '',
                                }));
                            }}
                            onFallbackInput={(val) => {
                                setNpForm((prev) => ({
                                    ...prev,
                                    city: val,
                                    nova_poshta_city_name: val,
                                }));
                            }}
                            error={npErrors.city}
                        />

                        <div className="space-y-1">
                            <Label>Тип точки доставки</Label>
                            <div className="flex gap-4">
                                <label className="flex cursor-pointer items-center gap-2">
                                    <input
                                        type="radio"
                                        name="np_delivery_point_type"
                                        value="branch"
                                        checked={
                                            npForm.delivery_point_type ===
                                            'branch'
                                        }
                                        onChange={() =>
                                            setNpForm({
                                                ...npForm,
                                                delivery_point_type: 'branch',
                                                delivery_point_number: '',
                                                nova_poshta_warehouse_ref: '',
                                                nova_poshta_warehouse_number:
                                                    '',
                                                nova_poshta_warehouse_address:
                                                    '',
                                            })
                                        }
                                        className="accent-primary"
                                    />
                                    <span className="text-sm">Відділення</span>
                                </label>
                                <label className="flex cursor-pointer items-center gap-2">
                                    <input
                                        type="radio"
                                        name="np_delivery_point_type"
                                        value="parcel_locker"
                                        checked={
                                            npForm.delivery_point_type ===
                                            'parcel_locker'
                                        }
                                        onChange={() =>
                                            setNpForm({
                                                ...npForm,
                                                delivery_point_type:
                                                    'parcel_locker',
                                                delivery_point_number: '',
                                                nova_poshta_warehouse_ref: '',
                                                nova_poshta_warehouse_number:
                                                    '',
                                                nova_poshta_warehouse_address:
                                                    '',
                                            })
                                        }
                                        className="accent-primary"
                                    />
                                    <span className="text-sm">Поштомат</span>
                                </label>
                            </div>
                        </div>

                        <NovaPoshtaWarehouseSelect
                            cityRef={npForm.nova_poshta_city_ref}
                            type={
                                npForm.delivery_point_type as
                                    | 'branch'
                                    | 'parcel_locker'
                            }
                            value={
                                npForm.nova_poshta_warehouse_number
                                    ? `${npForm.delivery_point_type === 'parcel_locker' ? 'Поштомат' : 'Відділення'} №${npForm.nova_poshta_warehouse_number}`
                                    : npForm.delivery_point_number
                            }
                            onSelect={(wh) => {
                                setNpForm((prev) => ({
                                    ...prev,
                                    delivery_point_number: wh.number,
                                    nova_poshta_warehouse_ref: wh.ref,
                                    nova_poshta_warehouse_number: wh.number,
                                    nova_poshta_warehouse_address: wh.address,
                                }));
                            }}
                            onFallbackInput={(val) => {
                                setNpForm((prev) => ({
                                    ...prev,
                                    delivery_point_number: val,
                                }));
                            }}
                            error={npErrors.delivery_point_number}
                            isFallback={!npForm.nova_poshta_city_ref}
                        />

                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            disabled={npSubmitForm.processing}
                            onClick={handleNpSave}
                        >
                            <Save className="mr-2 h-4 w-4" />
                            {npSubmitForm.processing
                                ? 'Збереження...'
                                : 'Зберегти'}
                        </Button>
                    </div>

                    {showUkrposhta && (
                        <>
                            <hr />

                            {/* Ukrposhta */}
                            <div className="space-y-4">
                                <DeliveryMethodSectionHeader
                                    title="Укрпошта"
                                    icon={Package}
                                    showClear={!!ukrposhta}
                                    onClear={() =>
                                        handleClear('ukrposhta', () =>
                                            setUpForm(toForm(null)),
                                        )
                                    }
                                />

                                {upSuccess && (
                                    <p className="text-sm font-medium text-green-600">
                                        {upSuccess}
                                    </p>
                                )}

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <DeliveryProfileField
                                        id="up_recipient_name"
                                        label="ПІБ отримувача"
                                        value={upForm.recipient_name}
                                        onChange={(v) =>
                                            setUpForm({
                                                ...upForm,
                                                recipient_name: v,
                                            })
                                        }
                                        error={upErrors.recipient_name}
                                        placeholder="Іванов Іван Іванович"
                                        required
                                    />
                                    <DeliveryProfileField
                                        id="up_recipient_phone"
                                        label="Телефон"
                                        value={upForm.recipient_phone}
                                        onChange={(v) =>
                                            setUpForm({
                                                ...upForm,
                                                recipient_phone: v,
                                            })
                                        }
                                        error={upErrors.recipient_phone}
                                        placeholder="+380991234567"
                                        required
                                    />
                                </div>

                                <UkrposhtaSettlementSelect
                                    value={
                                        upForm.ukrposhta_settlement_name ||
                                        upForm.city
                                    }
                                    onSelect={(s) => {
                                        setUpForm((prev) => ({
                                            ...prev,
                                            city: s.name,
                                            region: s.region,
                                            ukrposhta_settlement_ref: s.ref,
                                            ukrposhta_settlement_name: s.name,
                                            post_office: '',
                                            ukrposhta_post_office_ref: '',
                                            ukrposhta_post_office_index: '',
                                            ukrposhta_post_office_address: '',
                                            ukrposhta_post_office_name: '',
                                        }));
                                    }}
                                    onFallbackInput={(val) => {
                                        setUpForm((prev) => ({
                                            ...prev,
                                            city: val,
                                            ukrposhta_settlement_name: val,
                                        }));
                                    }}
                                    error={upErrors.city}
                                />

                                <UkrposhtaOfficeSelect
                                    settlementRef={
                                        upForm.ukrposhta_settlement_ref
                                    }
                                    value={
                                        upForm.ukrposhta_post_office_address
                                            ? `${upForm.ukrposhta_post_office_index ? upForm.ukrposhta_post_office_index + ' — ' : ''}${upForm.ukrposhta_post_office_address}`
                                            : upForm.post_office
                                    }
                                    onSelect={(office) => {
                                        setUpForm((prev) => ({
                                            ...prev,
                                            post_office: office.address,
                                            ukrposhta_post_office_ref:
                                                office.ref,
                                            ukrposhta_post_office_index:
                                                office.index,
                                            ukrposhta_post_office_address:
                                                office.address,
                                            ukrposhta_post_office_name:
                                                office.name,
                                        }));
                                    }}
                                    onFallbackInput={(val) => {
                                        setUpForm((prev) => ({
                                            ...prev,
                                            post_office: val,
                                        }));
                                    }}
                                    error={upErrors.post_office}
                                    isFallback={
                                        !upForm.ukrposhta_settlement_ref
                                    }
                                />

                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    disabled={upSubmitForm.processing}
                                    onClick={handleUpSave}
                                >
                                    <Save className="mr-2 h-4 w-4" />
                                    {upSubmitForm.processing
                                        ? 'Збереження...'
                                        : 'Зберегти'}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            )}
        </Card>
    );
};
